"use client";

import { c as _c } from "react/compiler-runtime";
import { CSSProperties, useRef } from "react";
import { motion } from "framer-motion";
import { Container, SectionSubHeading, SectionTitle } from "@/components";
import { serviceStages } from "@/data/ServiceStagesData";
import { useIsDesktop } from "@/hooks/ui/useIsDesktop";
import { useSectionProgress } from "@/hooks/animations/useSectionProgress";
import { cardRise, cardStagger, fadeInUp } from "@/lib/animation/variant";
const ServiceStages = () => {
  const $ = _c(29);
  const sectionRef = useRef(null);
  const isDesktop = useIsDesktop();
  const progress = useSectionProgress(sectionRef, isDesktop);
  const total = serviceStages.length;
  const position = progress * total;
  const active = serviceStages[Math.min(total - 1, Math.floor(position))];
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      once: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <motion.div custom={0} initial="hidden" whileInView="visible" viewport={t0} variants={fadeInUp}><SectionSubHeading subtitle="How the work runs" /></motion.div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      once: true
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="space-y-2">{t1}<motion.div custom={1} initial="hidden" whileInView="visible" viewport={t2} variants={fadeInUp}><SectionTitle title="Five stages. You go through them." className="max-w-4xl" /></motion.div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <section className="w-full bg-background pt-20 md:pt-28"><Container className="flex flex-col gap-8 px-4 lg:px-10">{t3}<motion.p custom={2} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="max-w-3xl text-base leading-relaxed font-medium text-muted-foreground md:text-lg">Every engagement runs the same five stages, whether the scope is a logo or a platform. Nothing is compressed to hit a date — if week eight says the concept is wrong, week eight is where it changes.</motion.p></Container></section>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== isDesktop) {
    t5 = isDesktop ? {
      height: `${total * 95 + 70}vh`
    } as CSSProperties : undefined;
    $[5] = isDesktop;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      once: true,
      amount: 0.15
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Container className="px-4 py-14 lg:hidden"><motion.div initial="hidden" whileInView="show" viewport={t6} variants={cardStagger} className="grid gap-5 md:grid-cols-2">{serviceStages.map(_temp)}</motion.div></Container>;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== position) {
    t8 = serviceStages.map((stage_0, index) => {
      const offset = position - index - 0.5;
      const opacity = Math.max(0, 1 - Math.abs(offset) * 2.2);
      const scale = 0.34 + Math.pow(Math.max(0, offset + 1), 1.9) * 1.1;
      return <div key={stage_0.number} style={{
        opacity,
        transform: `scale(${scale})`
      }} className="absolute inset-0 grid place-items-center will-change-transform"><span className={`text-[14vw] leading-[0.8] font-black tracking-tighter whitespace-nowrap uppercase ${opacity > 0.55 ? "text-primary" : "text-foreground"}`}>{stage_0.word}</span><span className="absolute inset-x-0 bottom-[15%] px-6 text-center font-mono text-[10px] leading-loose tracking-[0.28em] text-muted-foreground uppercase">{stage_0.points.join(" \xB7 ")}</span></div>;
    });
    $[9] = position;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== active.description) {
    t9 = <p className="absolute top-1/2 left-10 hidden max-w-[26ch] -translate-y-1/2 text-sm leading-loose text-muted-foreground xl:block">{active.description}</p>;
    $[11] = active.description;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const t10 = active.number;
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = String(total).padStart(2, "0");
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== active.number || $[15] !== active.word) {
    t12 = <span>Stage {t10} / {t11} —{" "}{active.word}</span>;
    $[14] = active.number;
    $[15] = active.word;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== active.weeks) {
    t13 = <span>{active.weeks}</span>;
    $[17] = active.weeks;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t12 || $[20] !== t13) {
    t14 = <div className="absolute inset-x-0 bottom-6 flex justify-between px-10 font-mono text-[10px] tracking-[0.24em] text-muted-foreground uppercase">{t12}{t13}</div>;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t14 || $[23] !== t8 || $[24] !== t9) {
    t15 = <div className="hidden lg:sticky lg:top-0 lg:grid lg:h-screen lg:place-items-center lg:overflow-hidden">{t8}{t9}{t14}</div>;
    $[22] = t14;
    $[23] = t8;
    $[24] = t9;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t15 || $[27] !== t5) {
    t16 = <>{t4}<section ref={sectionRef} style={t5} className="relative w-full bg-background" aria-label="Our five-stage process">{t7}{t15}</section></>;
    $[26] = t15;
    $[27] = t5;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  return t16;
};
export default ServiceStages;
function _temp(stage) {
  return <motion.article key={stage.number} variants={cardRise} className="flex flex-col gap-3 rounded-2xl border border-black/10 p-6"><div className="flex items-baseline justify-between gap-4"><span className="font-mono text-[11px] font-bold tracking-[0.24em] text-primary">{stage.number}</span><span className="font-mono text-[10px] tracking-[0.2em] text-muted-foreground uppercase">{stage.weeks}</span></div><h3 className="text-3xl leading-none font-black tracking-tighter uppercase">{stage.word}</h3><p className="text-sm leading-relaxed text-muted-foreground">{stage.description}</p><p className="mt-auto border-t border-black/10 pt-4 font-mono text-[9px] leading-loose tracking-[0.16em] text-muted-foreground uppercase">{stage.points.join(" \xB7 ")}</p></motion.article>;
}